import { InitPageStrict } from "../index";
import { KeonnCalibration, mmToPx, pxToMm } from "../floors/keonn_calibration";

type Point = [number, number];

interface OtherArea {
  name: string;
  points: Point[];
}

const SVG_NS = "http://www.w3.org/2000/svg";

function selfIntersects(points: Point[]): boolean {
  const side = (p: Point, q: Point, r: Point) => Math.sign((q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]));
  const crosses = (a: Point, b: Point, c: Point, d: Point) => side(a, b, c) * side(a, b, d) < 0 && side(c, d, a) * side(c, d, b) < 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    for (let j = i + 2; j < n; j++) {
      if (i === 0 && j === n - 1) continue;
      if (crosses(points[i], points[(i + 1) % n], points[j], points[(j + 1) % n])) return true;
    }
  }
  return false;
}

class SubZoneMapArea {
  private readonly root = $("#sub_zone_map_area");
  private readonly img = document.getElementById("sub_zone_map_area_img") as HTMLImageElement;
  private readonly overlay = document.getElementById("sub_zone_map_area_overlay") as unknown as SVGSVGElement;
  private readonly calibration: KeonnCalibration = JSON.parse(this.root.attr("data-calibration"));
  private readonly others: OtherArea[] = JSON.parse(this.root.attr("data-others"));
  private points: Point[] = JSON.parse(this.root.attr("data-points"));
  private saving = false;

  constructor() {
    this.bindEvents();
    this.render();
  }

  private bindEvents(): void {
    this.overlay.addEventListener("click", (e) => {
      const mm = this.toMm(e);
      if (!mm) return;
      this.points.push(mm);
      this.changed();
    });
    this.overlay.addEventListener("mousemove", (e) => {
      const mm = this.toMm(e);
      $("#sub_zone_map_area_cursor").text(mm ? `x ${this.fmt(mm[0])} · y ${this.fmt(mm[1])} mm` : "—");
    });
    this.overlay.addEventListener("mouseleave", () => $("#sub_zone_map_area_cursor").text("—"));
    this.img.addEventListener("load", () => this.render());
    $(window).off("resize.subZoneMapArea").on("resize.subZoneMapArea", () => this.render());
    $("#sub_zone_map_area_modal").off("shown.bs.modal.subZoneMapArea").on("shown.bs.modal.subZoneMapArea", () => this.render());

    $("#sub_zone_map_area_undo").on("click", () => {
      this.points.pop();
      this.changed();
    });
    $("#sub_zone_map_area_clear").on("click", () => {
      this.points = [];
      this.changed();
    });
    $("#sub_zone_map_area_list").on("click", ".sub-zone-map-area-del", (e) => {
      this.points.splice(Number($(e.currentTarget).data("index")), 1);
      this.changed();
    });
    $("#sub_zone_map_area_list").on("input", ".sub-zone-map-area-coord", (e) => this.editPoint(e.currentTarget as HTMLInputElement));
    $("#sub_zone_map_area_list").on("blur", ".sub-zone-map-area-coord", (e) => {
      const input = e.currentTarget as HTMLInputElement;
      input.value = String(this.points[Number(input.dataset.index)][Number(input.dataset.axis)]);
      $(input).removeClass("invalid");
    });
    $("#sub_zone_map_area_list").on("keydown", ".sub-zone-map-area-coord", (e) => {
      const input = e.currentTarget as HTMLInputElement;
      if (e.key === "Enter") input.blur();
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();
      const step = (e.shiftKey ? 100 : 1) * (e.key === "ArrowUp" ? 1 : -1);
      input.value = String(this.points[Number(input.dataset.index)][Number(input.dataset.axis)] + step);
      this.editPoint(input);
    });
    $("#sub_zone_map_area_add").on("click", () => this.addByValue());
    $("#sub_zone_map_area_x, #sub_zone_map_area_y").on("keydown", (e) => {
      if (e.key !== "Enter") return;
      e.preventDefault();
      this.addByValue();
    });
    $("#sub_zone_map_area_save").on("click", () => this.save());
  }

  private toMm(e: MouseEvent): Point | null {
    const rect = this.overlay.getBoundingClientRect();
    if (!rect.width || !this.img.naturalWidth) return null;
    const px = (e.clientX - rect.left) * this.img.naturalWidth / rect.width;
    const py = (e.clientY - rect.top) * this.img.naturalHeight / rect.height;
    return pxToMm(this.calibration, px, py);
  }

  private addByValue(): void {
    const x = String($("#sub_zone_map_area_x").val()).trim();
    const y = String($("#sub_zone_map_area_y").val()).trim();
    if (!/^-?\d+$/.test(x) || !/^-?\d+$/.test(y)) {
      this.message("Enter X and Y as whole numbers in mm.", "error");
      return;
    }
    this.points.push([parseInt(x, 10), parseInt(y, 10)]);
    $("#sub_zone_map_area_x, #sub_zone_map_area_y").val("");
    this.changed();
  }

  private editPoint(input: HTMLInputElement): void {
    const value = input.value.trim();
    const valid = /^-?\d+$/.test(value);
    $(input).toggleClass("invalid", !valid);
    if (!valid) return;
    this.points[Number(input.dataset.index)][Number(input.dataset.axis)] = parseInt(value, 10);
    this.message("", "");
    this.renderMap();
  }

  private changed(): void {
    this.message("", "");
    this.render();
  }

  private save(): void {
    if (this.saving) return;
    if (this.points.length > 0 && this.points.length < 3) {
      this.message("Add at least 3 points to make an area.", "error");
      return;
    }
    if (selfIntersects(this.points)) {
      this.message("Some lines cross. Remove points or add them in order around the edge.", "error");
      return;
    }

    this.setSaving(true);
    $.ajax({
      url: this.root.data("url"),
      method: "PATCH",
      dataType: "json",
      data: { floorplan_id: this.root.data("floorplan-id"), points: JSON.stringify(this.points) },
      headers: { "X-CSRF-Token": $("meta[name='csrf-token']").attr("content") || "" }
    })
      .done((body: { count: number }) => {
        const floorName = this.root.data("floor-name");
        if (body.count > 0) {
          this.message(`Area saved · ${body.count} points.`, "ok");
          const status = `${body.count} points · ${floorName}`;
          $("#sub_zone_map_area_status").text(status).attr("title", status).addClass("set");
        } else {
          this.message("Area removed.", "ok");
          $("#sub_zone_map_area_status").text("Not set").attr("title", "Not set").removeClass("set");
        }
      })
      .fail((xhr) => {
        const errors: string[] = xhr.responseJSON?.errors || ["Could not save the area."];
        this.message(errors.join(" "), "error");
      })
      .always(() => this.setSaving(false));
  }

  private setSaving(saving: boolean): void {
    this.saving = saving;
    $("#sub_zone_map_area_save").prop("disabled", saving).text(saving ? "Saving…" : "Save area");
  }

  private message(text: string, type: "" | "ok" | "error"): void {
    $("#sub_zone_map_area_msg").text(text).attr("class", `sub-zone-map-area-msg ${text ? `show ${type}` : ""}`);
  }

  private render(): void {
    this.renderMap();
    this.renderList();
  }

  private renderMap(): void {
    const width = this.img.naturalWidth;
    const height = this.img.naturalHeight;
    if (!width || !height) return;
    const k = this.img.clientWidth > 0 ? width / this.img.clientWidth : 1;
    this.overlay.setAttribute("viewBox", `0 0 ${width} ${height}`);

    const othersGroup = document.getElementById("sub_zone_map_area_others");
    othersGroup.innerHTML = "";
    this.others.forEach((other) => {
      const px = other.points.map((p) => mmToPx(this.calibration, p[0], p[1]));
      othersGroup.appendChild(this.svg("polygon", {
        points: px.map((p) => p.join(",")).join(" "),
        class: "sub-zone-map-area-other",
        "stroke-width": 2 * k,
        "stroke-dasharray": `${6 * k} ${4 * k}`
      }));
      const label = this.svg("text", {
        x: px.reduce((sum, p) => sum + p[0], 0) / px.length,
        y: px.reduce((sum, p) => sum + p[1], 0) / px.length,
        class: "sub-zone-map-area-other-label",
        "font-size": 14 * k,
        "text-anchor": "middle"
      });
      label.textContent = other.name;
      othersGroup.appendChild(label);
    });

    const px = this.points.map((p) => mmToPx(this.calibration, p[0], p[1]));
    const poly = document.getElementById("sub_zone_map_area_poly");
    poly.setAttribute("points", px.map((p) => p.join(",")).join(" "));
    poly.setAttribute("stroke-width", String(2.5 * k));

    const dots = document.getElementById("sub_zone_map_area_dots");
    dots.innerHTML = "";
    px.forEach((p, i) => {
      dots.appendChild(this.svg("circle", { cx: p[0], cy: p[1], r: 9 * k, "stroke-width": 2 * k }));
      const num = this.svg("text", { x: p[0], y: p[1] + 4 * k, "font-size": 11 * k, "text-anchor": "middle" });
      num.textContent = String(i + 1);
      dots.appendChild(num);
    });
  }

  private renderList(): void {
    $("#sub_zone_map_area_count").text(this.points.length);
    const list = $("#sub_zone_map_area_list").empty();
    if (!this.points.length) {
      list.append($("<div>", { class: "sub-zone-map-area-empty", text: "No points yet. Click on the map to add the first one." }));
    }
    this.points.forEach((p, i) => {
      list.append(
        $("<div>", { class: "sub-zone-map-area-pt" }).append(
          $("<span>", { class: "sub-zone-map-area-num", text: i + 1 }),
          $("<span>", { class: "sub-zone-map-area-val" }).append(
            this.coordInput(i, 0, "X"),
            $("<span>", { class: "sub-zone-map-area-sep", text: "," }),
            this.coordInput(i, 1, "Y")
          ),
          $("<button>", { type: "button", class: "sub-zone-map-area-del", text: "×", "aria-label": `Remove point ${i + 1}`, "data-index": i })
        )
      );
    });
  }

  private coordInput(index: number, axis: number, name: string): JQuery<HTMLElement> {
    return $("<input>").attr({
      type: "text",
      inputmode: "numeric",
      autocomplete: "off",
      class: "sub-zone-map-area-coord",
      value: this.points[index][axis],
      "aria-label": `Point ${index + 1} ${name} (mm)`,
      "data-index": index,
      "data-axis": axis
    });
  }

  private svg(tag: string, attrs: Record<string, string | number>): SVGElement {
    const el = document.createElementNS(SVG_NS, tag);
    Object.keys(attrs).forEach((key) => el.setAttribute(key, String(attrs[key])));
    return el;
  }

  private fmt(value: number): string {
    return value.toLocaleString("en-US");
  }
}

export function LoadPage() {
  InitPageStrict("zones", () => {
    $(document).on("sub_zone_map_area:loaded", () => {
      if (document.getElementById("sub_zone_map_area_overlay")) new SubZoneMapArea();
      $("#sub_zone_map_area_floor").on("change", (e) => {
        $.ajax({ url: $("#sub_zone_map_area").data("url"), data: { floorplan_id: $(e.currentTarget).val() }, dataType: "script" });
      });
    });
    $(document).on("hidden.bs.modal", "#sub_zone_map_area_modal", () => {
      $("#sub_zone_map_area_modal").remove();
      $("#zone_edit_form").modal("show");
    });
  });
}
